import { ExpenseBankSelect } from "@/components/expenses/ExpenseBankSelect";
import { DatePartsPicker } from "@/components/hr/DatePartsPicker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  EXPENSE_PAYMENT_METHODS,
  normalizeExpenseDate,
  paymentMethodMeta,
  paymentMethodUsesBank,
  type ExpensePaymentMethod,
} from "@/lib/expenses/expensesStore";
import type { BankOption } from "@/lib/sales/banksStore";
import { cn } from "@/lib/utils";

export type LoanPaymentDraft = {
  paymentMethod: ExpensePaymentMethod | "";
  bankId: string;
  referenceNo: string;
  chequeNo: string;
  chequeDate: string;
};

export const emptyLoanPaymentDraft = (): LoanPaymentDraft => ({
  paymentMethod: "",
  bankId: "",
  referenceNo: "",
  chequeNo: "",
  chequeDate: "",
});

export function LoanPaymentMethodFields({
  value,
  onChange,
  banks,
  compact = false,
}: {
  value: LoanPaymentDraft;
  onChange: (patch: Partial<LoanPaymentDraft>) => void;
  banks: BankOption[];
  compact?: boolean;
}) {
  const labelClass = compact ? "text-xs text-muted-foreground" : "text-xs font-bold text-foreground";
  const controlClass = compact ? "glass-input h-8 text-sm" : "glass-input h-10";
  const fieldGap = compact ? "space-y-1" : "space-y-2";

  return (
    <div className={cn(compact ? "grid grid-cols-1 gap-2" : "grid grid-cols-1 md:grid-cols-2 gap-4")}>
      <div className={cn(fieldGap, !compact && paymentMethodUsesBank(value.paymentMethod) && "md:col-span-2")}>
        <Label className={labelClass}>
          Payment method {compact ? null : <span className="text-destructive">*</span>}
        </Label>
        <Select
          value={value.paymentMethod || undefined}
          onValueChange={(v) => {
            const method = v as ExpensePaymentMethod;
            onChange({
              paymentMethod: method,
              bankId: paymentMethodUsesBank(method) ? value.bankId : "",
              referenceNo: method === "cheque" || method === "cash" ? "" : value.referenceNo,
              chequeNo: method === "cheque" ? value.chequeNo : "",
              chequeDate: method === "cheque" ? value.chequeDate : "",
            });
          }}
        >
          <SelectTrigger className={controlClass}>
            <SelectValue placeholder="Select payment method" />
          </SelectTrigger>
          <SelectContent>
            {EXPENSE_PAYMENT_METHODS.map((method) => (
              <SelectItem key={method.value} value={method.value}>
                {method.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {value.paymentMethod && !compact ? (
          <p className="text-[11px] text-muted-foreground">
            Account/Channel: {paymentMethodMeta(value.paymentMethod)?.channel}
          </p>
        ) : null}
      </div>

      {value.paymentMethod && !paymentMethodUsesBank(value.paymentMethod) ? (
        <div className={fieldGap}>
          <Label className="text-xs text-muted-foreground">Account / Channel</Label>
          <Input className={controlClass} value="Cash" disabled />
        </div>
      ) : null}

      {paymentMethodUsesBank(value.paymentMethod) ? (
        <div
          className={cn(
            "grid grid-cols-1",
            compact ? "gap-2" : "md:col-span-2 gap-4",
            value.paymentMethod === "cheque" ? (compact ? "sm:grid-cols-3" : "md:grid-cols-3") : compact ? "sm:grid-cols-2" : "md:grid-cols-2",
          )}
        >
          <div className={cn(fieldGap, "min-w-0")}>
            <Label className="text-xs text-muted-foreground">
              Bank {compact ? null : <span className="text-destructive">*</span>}
            </Label>
            <ExpenseBankSelect
              value={value.bankId}
              banks={banks}
              onChange={(id) => onChange({ bankId: id })}
              triggerClassName={compact ? "h-8" : undefined}
            />
            {banks.length === 0 ? (
              <p className="text-[11px] text-destructive">No banks found. Add a bank first.</p>
            ) : null}
          </div>

          {value.paymentMethod === "cheque" ? (
            <>
              <div className={cn(fieldGap, "min-w-0")}>
                <Label className="text-xs text-muted-foreground">
                  Cheque # {compact ? null : <span className="text-destructive">*</span>}
                </Label>
                <Input
                  className={controlClass}
                  value={value.chequeNo}
                  onChange={(e) => onChange({ chequeNo: e.target.value })}
                  placeholder="Cheque number"
                />
              </div>
              <div className={cn(fieldGap, "min-w-0")}>
                <Label className="text-xs text-muted-foreground">Cheque date</Label>
                <DatePartsPicker
                  value={value.chequeDate}
                  onChange={(iso) => onChange({ chequeDate: normalizeExpenseDate(iso) })}
                  fromYear={1980}
                  toYear={new Date().getFullYear() + 1}
                  placeholder="Optional"
                />
              </div>
            </>
          ) : (
            <div className={cn(fieldGap, "min-w-0")}>
              <Label className="text-xs text-muted-foreground">
                {paymentMethodMeta(value.paymentMethod)?.referenceLabel}{" "}
                {compact ? null : <span className="text-destructive">*</span>}
              </Label>
              <Input
                className={controlClass}
                value={value.referenceNo}
                onChange={(e) => onChange({ referenceNo: e.target.value })}
                placeholder={paymentMethodMeta(value.paymentMethod)?.referenceLabel ?? "Reference"}
              />
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
